/**
 * [AUTO-REFRESH 2026-08-03] "Is it safe to reload this tab right now?"
 *
 * The update banner may reload the page on its own (see UpdateBanner). A reload throws away
 * everything in memory, so it must never fire while the user has work in progress. This
 * module is the single place that answers that question, and it answers it GLOBALLY — there
 * is no per-page wiring to keep in sync, because every signal it reads is app-wide:
 *
 *   1. IN-FLIGHT WRITES — services/api.ts counts every PUT/POST/PATCH/DELETE through one
 *      pair of axios interceptors. Reloading mid-write would abort the request from the
 *      browser's side while the server may still commit it, leaving the user with no idea
 *      whether their change landed. This is the important one.
 *   2. OPEN MODALS/DRAWERS — every modal overlay in the app renders with a `data-reload-block`
 *      attribute (added once on each styled Overlay via `.attrs`), so one querySelector sees
 *      them all: Add Payment, Mapping, Add/Bulk Inventory, renewal, confirm dialogs, the
 *      trash drawer, the notification panel, the mobile record sheet.
 *   3. AN OPEN EDITOR — if focus is sitting in an input/select/textarea (an inline table cell
 *      being edited, a search box mid-type), the user is typing. Covers every page's inline
 *      edit without any page having to register anything.
 *
 * Deliberately NOT a "block everything" list: filters, scroll position and the selected tab
 * are not work-in-progress, and blocking on those would mean the auto-reload never fires.
 */

// Count of mutating HTTP requests currently in flight. Maintained by services/api.ts.
let inFlightMutations = 0;

/** Called by the api interceptor when a mutating request starts. */
export function noteMutationStart(): void {
  inFlightMutations += 1;
}

/** Called by the api interceptor when a mutating request settles (success OR failure). */
export function noteMutationEnd(): void {
  // Never let a stray decrement push this negative — that would permanently mask a real
  // in-flight write and make the guard claim "safe" when it is not.
  inFlightMutations = Math.max(0, inFlightMutations - 1);
}

/** Escape hatch for anything that needs to block a reload without an HTTP request or a
 *  modal — e.g. a long-running client-side export. Pair every hold() with its release(). */
const holds = new Set<string>();
export function holdReload(key: string): void { holds.add(key); }
export function releaseReload(key: string): void { holds.delete(key); }

function hasOpenOverlay(): boolean {
  try {
    return !!document.querySelector('[data-reload-block]');
  } catch {
    return false; // no DOM (SSR/tests) → don't let the check itself block
  }
}

function isTypingSomewhere(): boolean {
  try {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    return el.isContentEditable === true;
  } catch {
    return false;
  }
}

/** True only when nothing on screen or in flight would be lost by reloading now. */
export function isReloadSafe(): boolean {
  return inFlightMutations === 0 && holds.size === 0 && !hasOpenOverlay() && !isTypingSomewhere();
}

/** Why a reload was refused — for the console breadcrumb only, never shown to users. */
export function reloadBlockReason(): string | null {
  if (inFlightMutations > 0) return `${inFlightMutations} write(s) in flight`;
  if (holds.size > 0) return `held by ${[...holds].join(', ')}`;
  if (hasOpenOverlay()) return 'a dialog is open';
  if (isTypingSomewhere()) return 'a field has focus';
  return null;
}
